import React, { useMemo, useRef } from 'react';
import { Box, Button, Checkbox, TableSortLabel } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
// import { useDrag, useDrop } from 'react-dnd';
import { ReactComponent as sortIcon } from '../../../assets/icons/sortTableIcon.svg';
import classNames from 'classnames';
import { ColumnsLabel, CheckBoxStates } from './constants';
import { Theme } from '@mui/material/styles/createTheme';
import { TypeOrder } from './interface';

const flexAlignment: any = {
  left: 'start',
  center: 'center',
  right: 'end',
};

const useStyles = makeStyles<Theme | any>()(
  (
    theme,
    { isDragging, isDragTarget, draggable, isActiveOrder, alignHeaderLabel },
  ): any => ({
    cellBox: {
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
      justifyContent: flexAlignment[alignHeaderLabel],
      fontSize: 12,
      minWidth: 'max-content',
      fontWeight: 700,
      cursor: draggable ? 'grab' : 'default',
      '&:active, & label:active': {
        cursor: draggable ? 'grabbing' : 'default',
      },
      '& label': {
        cursor: draggable ? 'grab' : 'default',
        zIndex: isDragTarget ? 2 : 0,
      },
      '&::before': {
        position: 'absolute',
        display: isDragging || isDragTarget ? 'block' : 'none',
        height: 'calc(100% + 12px)',
        width: 'calc(100% + 32px)',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        border: `dashed 1px ${theme.palette.primary.main}`,
        content: '""',
        backgroundColor: theme.palette.grey[400],
        zIndex: 1,
      },
      '&:hover svg': {
        opacity: '1 !important',
      },
    },
    orderLabel: {
      color: `${theme.palette.text.primary} !important`,
      width: 24,
      height: 24,
      marginLeft: theme.spacing(0.4),
      zIndex: isDragTarget ? 2 : 0,
      '& svg': {
        margin: 0,
        width: 'inherit',
        height: 'inherit',
        opacity: `${isActiveOrder ? 0.3 : 0} !important`,
        '& path:first-of-type': {
          fill: isActiveOrder && theme.palette.text.primary,
        },
      },
    },
    selectAllCheckBox: {
      color: theme.palette.primary.main,
    },
  }),
);

interface IHeaderCell {
  id: string;
  orderBy: string;
  onSort: (a: any, b: any) => any;
  sortable: boolean;
  sortingKey: string;
  order: TypeOrder;
  label?: string;
  index: number;
  onSortColumns: (dragId: string, destinationId: string) => void;
  onClickAllRows?: () => void;
  className?: string;
  draggable?: boolean;
  onSelectAllRows?: () => void;
  selectAllValue?: number;
  disableSelectAll?: boolean;
  alignHeaderLabel?: any;
}

interface IDropItem {
  id: string;
  index: number;
}

const HeaderCell = ({
  orderBy,
  onSort,
  sortable,
  sortingKey,
  order,
  label,
  id,
  index,
  onSortColumns,
  onClickAllRows = () => false,
  className = '',
  draggable = false,
  onSelectAllRows = () => false,
  selectAllValue = 0, //0-false, 1-true
  disableSelectAll = false,
  alignHeaderLabel = 'left',
}: IHeaderCell) => {
  const ref = useRef(null);
  // const [{ isOver, canDrop }, drop] = useDrop({
  //   accept: 'card',
  //   drop: (item: IDropItem) => {
  //     const dragId = item.id;
  //     const destinationId = id;
  //     if (dragId === destinationId) {
  //       return;
  //     }
  //     onSortColumns(dragId, destinationId);
  //   },
  //   canDrop: (item: IDropItem) => draggable && item.id !== id,
  //   collect: (monitor) => ({
  //     isOver: monitor.isOver(),
  //     canDrop: monitor.canDrop(),
  //   }),
  // });
  // const [{ isDragging }, drag] = useDrag({
  //   type: 'card',
  //   item: () => ({ id, index }),
  //   collect: (monitor) => ({
  //     isDragging: monitor.isDragging(),
  //   }),
  // });
  // const isDragTarget = isOver && canDrop;

  const { classes }: any = useStyles({
    // isDragging,
    // isDragTarget,
    draggable,
    isActiveOrder: orderBy === sortingKey,
    alignHeaderLabel,
  });

  const columnName = useMemo(() => {
    switch (label) {
      case ColumnsLabel.SELECT_ALL:
      case ColumnsLabel.DESELECT_ALL:
        return (
          <Button
            variant="text"
            onClick={() => onClickAllRows()}
            style={{
              textTransform: 'none',
              padding: '0px',
              margin: '7px 8px',
            }}>
            {label}
          </Button>
        );
      case 'Select all check box':
        return (
          <Checkbox
            disabled={disableSelectAll}
            checked={selectAllValue === CheckBoxStates.SELECTED_ALL}
            indeterminate={selectAllValue === CheckBoxStates.INDETERMINATE}
            onChange={() => onSelectAllRows()}
            color="primary"
            className={classes.selectAllCheckBox}
          />
        );
    }
    return (
      <label
        style={{ textAlign: alignHeaderLabel }}
        dangerouslySetInnerHTML={{
          __html: label ? label.toUpperCase() : '',
        }}
      />
    );
  }, [label, selectAllValue, onSelectAllRows]);

  // draggable && drag(drop(ref));

  return (
    <div id={id} ref={ref} draggable={draggable}>
      <Box className={classNames(className, classes?.cellBox)}>
        {columnName}
        {sortable && (
          <TableSortLabel
            IconComponent={sortIcon}
            className={classes?.orderLabel}
            active={sortable}
            direction={orderBy === sortingKey ? order : 'desc'}
            onClick={onSort(order, sortingKey)}
          />
        )}
      </Box>
    </div>
  );
};

export default HeaderCell;
